import { useMemo, useState } from 'react'
import { Link, Navigate, useSearchParams } from 'react-router'
import { Button } from '../../components/ui'
import { RELAY, mapTablePath, nodePath, relayKeys, type LinkState, type RelayLink, type RelayList } from './api'
import { RelayDataBanner } from './banner'
import { ago, bps, clock, ms, pct } from './format'
import { useUrlParams } from '../params'
import { useRelayLive } from './live'
import { LinkPanel } from './NodeLinks'
import { errorText, useRelayGet } from './queries'
import { LINK_REASON, LINK_SPEC } from './states'
import { Empty, ErrorBox, SkeletonRows, StateBadge } from './ui'

/*
 * Network › Map › Table (handoff/map.md: Table is R0; Topology, Geography and
 * Matrix are R2 and not shown): every link between nodes as a row — from → to,
 * state with its reason in words, RTT and hop latency p50 / p99, retransmits and
 * the loss estimate, traffic, legs up, routes, since / the last failure — worst
 * first. Live from SSE link / link_removed; without the stream polled every 5 s.
 * A row (click, Enter) opens the link panel of R0.13b below the table (?link=).
 */

type StateFilter = '' | 'problems' | LinkState
const ORDER: LinkState[] = ['down', 'degraded', 'unknown', 'up', 'idle']
const PROBLEMS: LinkState[] = ['down', 'degraded']
const CHIPS: { value: StateFilter; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'problems', label: 'Problems' },
  { value: 'down', label: 'Down' },
  { value: 'degraded', label: 'Degraded' },
  { value: 'up', label: 'Up' },
  { value: 'idle', label: 'Idle' },
  { value: 'unknown', label: 'No data' },
]
const SEL = 'min-h-9 rounded-md border border-line-2 bg-bg px-2 text-[13px] text-text-soft focus-visible:border-accent max-md:min-h-[44px]'

export function sortLinks(ls: RelayLink[]): RelayLink[] {
  return [...ls].sort((a, b) => ORDER.indexOf(a.state) - ORDER.indexOf(b.state) || a.from.name.localeCompare(b.from.name) || a.to.name.localeCompare(b.to.name))
}

/** /admin/network/map: R0 has the Table view only; any other view lands on it. */
export function MapPage() {
  const [p] = useSearchParams()
  if (p.get('view') !== 'table') {
    const rest = Object.fromEntries([...p].filter(([k]) => k !== 'view'))
    return <Navigate to={mapTablePath(rest)} replace />
  }
  return <MapTable />
}

function MapTable() {
  const live = useRelayLive()
  const { params: p, update } = useUrlParams()
  const sel = p.get('link')
  const state = (p.get('state') ?? '') as StateFilter
  const region = p.get('region') ?? ''
  const q = p.get('q') ?? ''
  const [opened, setOpened] = useState(0)
  const set = (patch: Record<string, string | null>) => update(patch)
  const select = (id: string | null) => {
    if (id) setOpened((x) => x + 1)
    set({ link: id })
  }

  const list = useRelayGet<RelayList<RelayLink>>(relayKeys.links, `${RELAY}/links?limit=500`, live, { poll: true })
  const fromLive = live.sse === 'open' && live.loaded
  const links = useMemo(() => (fromLive ? [...live.links.values()] : (list.data?.data.items ?? [])), [fromLive, live.links, list.data])
  const regions = useMemo(() => [...new Set(links.flatMap((l) => [l.from.region, l.to.region]).filter(Boolean))].sort(), [links])
  const ql = q.trim().toLowerCase()
  const scoped = useMemo(
    () =>
      links.filter(
        (l) =>
          (!region || l.from.region === region || l.to.region === region) &&
          (!ql || `${l.link_id} ${l.from.name} ${l.to.name} ${l.from.id} ${l.to.id}`.toLowerCase().includes(ql)),
      ),
    [links, region, ql],
  )
  const counts = useMemo(() => {
    const c: Record<string, number> = { '': scoped.length, problems: 0 }
    for (const l of scoped) {
      c[l.state] = (c[l.state] ?? 0) + 1
      if (PROBLEMS.includes(l.state)) c.problems!++
    }
    return c
  }, [scoped])
  const shown = useMemo(() => sortLinks(scoped.filter((l) => (state === 'problems' ? PROBLEMS.includes(l.state) : !state || l.state === state))), [scoped, state])
  const filtered = !!(state || region || q)
  const reset = () => set({ state: null, region: null, q: null })
  const loading = !fromLive && list.isPending && live.available !== false
  const failed = !fromLive && list.error && !list.data

  return (
    <div className="flex flex-col gap-4" data-testid="network-map" data-view="table" data-source={fromLive ? 'live' : 'poll'}>
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-semibold">Map</h1>
        <div role="tablist" aria-label="Map view" className="flex gap-1">
          <Link
            role="tab"
            aria-selected
            to={mapTablePath()}
            data-testid="map-view-table"
            className="inline-flex min-h-8 items-center rounded-md border border-live/50 bg-live/[.12] px-3 text-[13px] font-medium text-live max-md:min-h-[44px]"
          >
            Table
          </Link>
        </div>
        <span className="ml-auto text-xs text-muted">{fromLive ? 'live' : live.available === false ? '' : 'polling every 5 s'}</span>
      </div>
      <RelayDataBanner live={live} />

      {live.available === false ? (
        <Empty title="No routing network on this center" testId="map-unavailable">
          This center has no relay API (GET /api/admin/relay/settings answered 404). Links appear after an update of the center with the routing network.
        </Empty>
      ) : (
        <>
          {!loading && !failed && links.length > 0 && (
            <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Filter by state">
              {CHIPS.filter((c) => c.value === '' || c.value === 'problems' || (counts[c.value] ?? 0) > 0 || state === c.value).map((c) => {
                const spec = c.value && c.value !== 'problems' ? LINK_SPEC[c.value] : null
                return (
                  <button
                    key={c.value || 'all'}
                    type="button"
                    aria-pressed={state === c.value}
                    data-testid={`links-chip-${c.value || 'all'}`}
                    onClick={() => set({ state: c.value || null })}
                    className={`num inline-flex min-h-8 items-center gap-1.5 rounded-md border px-3 text-[13px] max-md:min-h-[44px] ${state === c.value ? 'border-live/50 bg-live/[.12] text-live' : 'border-line-2 text-text-soft hover:text-text'}`}
                  >
                    {spec && <span aria-hidden>{spec.glyph}</span>}
                    {c.label} <span className="font-mono">{counts[c.value] ?? 0}</span>
                  </button>
                )
              })}
            </div>
          )}

          <section className="rs-corners min-w-0 rounded-lg border border-line" aria-labelledby="map-links-h">
            <div className="flex flex-wrap items-center gap-2 border-b border-line px-4 py-3">
              <h2 id="map-links-h" className="mr-2 text-[15px] font-semibold">
                Links
              </h2>
              <label className="flex min-h-9 min-w-0 flex-1 basis-[200px] items-center gap-2 rounded-md border border-line-2 bg-bg px-2.5 focus-within:border-accent focus-within:shadow-focus max-md:min-h-[44px] md:max-w-[280px]">
                <span className="text-muted" aria-hidden>
                  ⌕
                </span>
                <input
                  value={q}
                  onChange={(e) => set({ q: e.target.value || null })}
                  placeholder="Node name or id"
                  aria-label="Search links"
                  data-testid="links-search"
                  className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted"
                />
              </label>
              <select aria-label="Region" data-testid="links-region" className={SEL} value={region} onChange={(e) => set({ region: e.target.value || null })}>
                <option value="">All regions</option>
                {regions.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
              {filtered && (
                <Button variant="quiet" className="max-md:min-h-[44px]" data-testid="links-reset" onClick={reset}>
                  Reset filters
                </Button>
              )}
              <span className="num ml-auto text-xs text-muted" data-testid="links-shown">
                {shown.length} shown · worst first
              </span>
            </div>
            {loading ? (
              <div className="p-4">
                <SkeletonRows rows={4} cols={8} />
              </div>
            ) : failed ? (
              <div className="p-4">
                <ErrorBox testId="links-error" text={errorText(`${RELAY}/links`, list.error)} busy={list.isFetching} onRetry={() => void list.refetch()} />
              </div>
            ) : links.length === 0 ? (
              <div className="p-4">
                <Empty title="No links yet" testId="links-empty">
                  A link appears when the first relay leg goes between two nodes: a stream on an edge node with egress via center, served by a hub. Links without legs for an
                  hour leave the list.
                </Empty>
              </div>
            ) : shown.length === 0 ? (
              <div className="p-4">
                <Empty title="No links match the filters" testId="links-none-match" action={<Button onClick={reset}>Reset filters</Button>} />
              </div>
            ) : (
              <>
                <LinksTable links={shown} sel={sel} onOpen={(id) => select(sel === id ? null : id)} />
                <LinkCards links={shown} sel={sel} onOpen={(id) => select(sel === id ? null : id)} />
              </>
            )}
          </section>
          {sel && <LinkPanel id={sel} live={live} focus={opened} onClose={() => select(null)} />}
        </>
      )}
    </div>
  )
}

const num = 'num text-right font-mono text-xs whitespace-nowrap'

function LinksTable({ links, sel, onOpen }: { links: RelayLink[]; sel: string | null; onOpen: (id: string) => void }) {
  const cols = [
    ['link', 'Link', 19],
    ['state', 'State', 14],
    ['rtt', 'RTT p50 / p99', 10],
    ['lat', 'Latency p50 / p99', 11],
    ['retr', 'Retrans / loss', 9],
    ['bps', 'Traffic', 8],
    ['legs', 'Legs', 6],
    ['routes', 'Routes', 6],
    ['since', 'Since · last failure', 13],
  ] as const
  const sum = cols.reduce((a, c) => a + c[2], 0)
  return (
    <div className="hidden md:block">
      <table data-testid="links-table" className="w-full table-fixed text-left text-sm">
        <caption className="sr-only">Links between nodes, worst first</caption>
        <colgroup>
          {cols.map((c) => (
            <col key={c[0]} style={{ width: `${((c[2] / sum) * 100).toFixed(2)}%` }} />
          ))}
        </colgroup>
        <thead className="border-b border-line text-xs text-muted">
          <tr className="[&>th]:px-3 [&>th]:py-2 [&>th]:font-medium [&>th:first-child]:pl-4 [&>th:last-child]:pr-4">
            {cols.map((c) => (
              <th scope="col" key={c[0]} className={['rtt', 'lat', 'retr', 'bps', 'legs', 'routes'].includes(c[0]) ? 'text-right' : ''}>
                {c[1]}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-line">
          {links.map((l) => {
            const down = l.state === 'down'
            const open = sel === l.link_id
            return (
              <tr
                key={l.link_id}
                data-testid={`map-link-${l.link_id}`}
                data-state={l.state}
                aria-selected={open}
                tabIndex={0}
                onClick={(e) => {
                  if (!(e.target as HTMLElement).closest('a')) onOpen(l.link_id)
                }}
                onKeyDown={(e) => {
                  if (e.target !== e.currentTarget) return
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    onOpen(l.link_id)
                  }
                }}
                className={`cursor-pointer outline-none focus-visible:bg-accent/[.06] focus-visible:shadow-focus [&>td]:overflow-hidden [&>td]:px-3 [&>td]:py-2 [&>td]:align-top [&>td:first-child]:pl-4 [&>td:last-child]:pr-4 ${open ? 'bg-accent/[.06]' : 'hover:bg-panel'}`}
              >
                <td>
                  <span className="flex min-w-0 flex-wrap items-baseline gap-x-1 font-mono text-[13px] font-semibold">
                    <Link to={nodePath(l.from.id)} className="truncate hover:underline">
                      {l.from.name}
                    </Link>
                    <span className="text-muted" aria-label="to">
                      →
                    </span>
                    <Link to={nodePath(l.to.id)} className="truncate hover:underline">
                      {l.to.name}
                    </Link>
                  </span>
                  <span className="block truncate font-mono text-[11px] text-muted">
                    {l.from.region} → {l.to.region}
                  </span>
                </td>
                <td>
                  <span className="flex min-w-0 flex-col items-start gap-1">
                    <StateBadge spec={LINK_SPEC[l.state]} />
                    {l.state_reason && <span className={`text-[11px] leading-snug ${down ? 'text-fail' : 'text-warn'}`}>{LINK_REASON[l.state_reason]}</span>}
                  </span>
                </td>
                <td className={num}>{down ? '—' : `${ms(l.rtt_p50_ms)} / ${ms(l.rtt_p99_ms)}`}</td>
                <td className={num}>{down ? '—' : `${ms(l.lat_p50_ms)} / ${ms(l.lat_p99_ms)}`}</td>
                <td className={`${num} ${(l.retrans_pct ?? 0) >= 1 ? 'text-warn' : ''}`}>{down ? '—' : `${pct(l.retrans_pct)} / ${pct(l.loss_pct)}`}</td>
                <td className={num}>{l.util_bps ? bps(l.util_bps) : '—'}</td>
                <td className={`${num} ${l.legs > l.legs_up ? 'text-warn' : ''}`}>
                  {l.legs_up}/{l.legs}
                </td>
                <td className={num} title={l.route_ids.join(', ')}>
                  {l.routes}
                </td>
                <td className="text-xs text-muted">
                  <span className="num block font-mono whitespace-nowrap">{down ? clock(l.since) : ago(l.since)}</span>
                  {l.last_failure && (
                    <span className="block truncate text-[11px]" title={`${clock(l.last_failure.at)}: ${l.last_failure.reason}`}>
                      ✕ {ago(l.last_failure.at)} · {l.last_failure.reason.replace(/_/g, ' ')}
                    </span>
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function LinkCards({ links, sel, onOpen }: { links: RelayLink[]; sel: string | null; onOpen: (id: string) => void }) {
  return (
    <ul className="divide-y divide-line md:hidden" data-testid="links-cards">
      {links.map((l) => {
        const down = l.state === 'down'
        return (
          <li key={l.link_id} data-testid={`map-link-card-${l.link_id}`} data-state={l.state}>
            <button type="button" aria-expanded={sel === l.link_id} onClick={() => onOpen(l.link_id)} className="flex min-h-[44px] w-full flex-col gap-1.5 px-4 py-3 text-left">
              <span className="flex items-start justify-between gap-2">
                <span className="min-w-0 truncate font-mono text-sm font-semibold">
                  {l.from.name} → {l.to.name}
                </span>
                <StateBadge spec={LINK_SPEC[l.state]} />
              </span>
              {l.state_reason && <span className={`text-xs ${down ? 'text-fail' : 'text-warn'}`}>{LINK_REASON[l.state_reason]}</span>}
              <span className="num flex flex-wrap gap-x-3 font-mono text-xs text-text-soft">
                <span>rtt {down ? '—' : `${ms(l.rtt_p50_ms)}/${ms(l.rtt_p99_ms)}`}</span>
                <span>retr {down ? '—' : pct(l.retrans_pct)}</span>
                <span>
                  legs {l.legs_up}/{l.legs}
                </span>
                <span>routes {l.routes}</span>
              </span>
            </button>
          </li>
        )
      })}
    </ul>
  )
}
